import styled from "styled-components";
import { Typography } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import BadgeIcon from "@mui/icons-material/Badge";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ShareMuiIcon from "@mui/icons-material/Share";
import { theme } from "../../utils/globalStyles/colors";
import { fadeIn, fadeInUp } from "../../utils/globalStyles/animations";

export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

export const Label = styled.label`
  color: #555555;
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 8px 4px;
`;

export const SearchRow = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
  gap: 10px;
`;

export const CodeInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 52px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  background-color: #ffffff;
  padding: 0 16px;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 22px; /* >= 16px evita o zoom automático do iOS ao focar */
  font-weight: 800;
  letter-spacing: 8px;
  color: #333333;
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: #d0d0d0;
  }

  &:focus {
    border: 2px solid ${theme.colors.base};
    padding: 0 15px;
  }
`;

export const SearchButton = styled.button<{ $inactive: boolean }>`
  flex-shrink: 0;
  height: 52px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: ${({ $inactive }) => ($inactive ? "default" : "pointer")};
  background-color: ${({ $inactive }) =>
    $inactive ? "#e0e0e0" : theme.colors.base};
  color: ${({ $inactive }) => ($inactive ? "#999999" : "#ffffff")};
  transition:
    background-color 0.2s ease,
    transform 0.15s ease;

  &:active {
    transform: ${({ $inactive }) => ($inactive ? "none" : "scale(0.96)")};
  }
`;

export const SearchIcon = styled(SearchRoundedIcon)`
  font-size: 20px !important;
`;

export const Helper = styled(Typography)`
  color: #999999;
  font-size: 12px !important;
  margin: 8px 4px 20px !important;
`;

/* Resultado da busca */
export const ResultCard = styled.div`
  width: 100%;
  background-color: #ffffff;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  animation: ${fadeInUp} 0.3s ease-out backwards;
`;

export const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const InfoGroup = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Name = styled(Typography)`
  font-weight: 800 !important;
  color: #333333;
  font-size: 17px !important;
  word-break: break-word;
`;

export const SubInfo = styled(Typography)`
  color: #888888;
  font-size: 13px !important;
  font-weight: 600 !important;
`;

export const Badge = styled.div<{ $success: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $success }) => ($success ? "#28a745" : "#666666")};
  animation: ${fadeIn} 0.3s ease-out both;
`;

export const AddedIcon = styled(CheckCircleIcon)`
  font-size: 20px !important;
  color: #28a745;
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
`;

const baseButton = `
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:active {
    transform: scale(0.97);
  }
`;

export const PrimaryButton = styled.button`
  ${baseButton}
  flex: 1;
  border: none;
  background-color: ${theme.colors.base};
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(192, 90, 33, 0.25);
`;

export const SecondaryButton = styled.button`
  ${baseButton}
  flex-shrink: 0;
  padding: 0 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #ffffff;
  color: #555555;
`;

export const AddIcon = styled(PersonAddIcon)`
  font-size: 20px !important;
`;

export const SendIcon = styled(SendRoundedIcon)`
  font-size: 20px !important;
`;

/* Mensagens (não encontrado, próprio código, erro) */
export const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 16px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease-out both;
`;

export const NotFoundIcon = styled(PersonSearchIcon)`
  color: #d0d0d0;
  font-size: 48px !important;
`;

export const SelfIcon = styled(BadgeIcon)`
  color: ${theme.colors.base};
  opacity: 0.6;
  font-size: 48px !important;
`;

export const MessageTitle = styled(Typography)`
  color: #555555;
  font-size: 15px !important;
  font-weight: 700 !important;
`;

export const MessageText = styled(Typography)`
  color: #999999;
  font-size: 14px !important;
`;

export const RetryButton = styled.button`
  margin-top: 6px;
  border: 1px solid ${theme.colors.base};
  background: none;
  color: ${theme.colors.base};
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`;

/* Código do próprio usuário, para passar para quem vai adicioná-lo */
export const OwnCode = styled.div`
  margin-top: 28px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px dashed rgba(192, 90, 33, 0.35);
  background-color: rgba(192, 90, 33, 0.04);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

export const OwnCodeText = styled(Typography)`
  color: #666666;
  font-size: 14px !important;

  & strong {
    color: ${theme.colors.base};
    font-size: 16px;
    letter-spacing: 1px;
  }
`;

export const OwnCodeActions = styled.div`
  display: flex;
  gap: 4px;
`;

export const LinkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  color: ${theme.colors.base};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;

  &:active {
    background-color: rgba(192, 90, 33, 0.1);
  }
`;

export const CopyIcon = styled(ContentCopyIcon)`
  font-size: 16px !important;
`;

export const CopiedIcon = styled(CheckIcon)`
  font-size: 16px !important;
  color: #28a745;
`;

export const ShareIcon = styled(ShareMuiIcon)`
  font-size: 16px !important;
`;
